// DrugReference — My-pharmacy reference table (D-DRUGREF-1, Patrick 2026-07-13).
// Gated by the 'drugreference' module (route wraps it in <RequireModule module="drugreference">).
// Read/write, Pioneer-fed + manual. Category is admin-extensible; new/unclassified drugs are
// flagged. IsActive is yes/no (its workflow-stopping effect is a Phase-2 build).
import React, { useCallback, useEffect, useState, useMemo } from 'react'
import * as api from '../../api/client'
import type { DrugRefRow, DrugRefClinical } from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useServerPaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { listScrollStyle } from '../../styles/listScroll'
import DrugRecordEditV4 from './DrugRecordEditV4'
import { normalizeAnalytes, analytePill } from '../../lib/analytes'
import { useServerFunnels, type ServerFunnelColumn } from '../../components/common/TableFunnels'
import { useAuth } from '../../auth/AuthContext'
import Notice from '../../components/ui/Notice'


const T = { text: 'var(--color-text, #1a1a1a)', soft: 'var(--color-text-soft, #6b7280)', border: 'var(--color-border, #e5e7eb)', surface: 'var(--color-surface, #fff)', warnBg: 'var(--color-warning-bg, #fef3c7)', warnFg: 'var(--color-warning-fg, #92400e)' }

type ColumnDef = { id: string; field_name: string; field_type: string; dropdown_options: string | null; sort_order: number }
type Category = { name: string; sort_order: number; is_global: boolean; color: string | null }

const CAT_COLOR: Record<string, [string, string]> = {
  REMS: ['#E6F1FB', '#0C447C'], Specialty: ['#EEEDFE', '#3C3489'], Procedural: ['#FAEEDA', '#633806'],
  Formulary: ['#E1F5EE', '#085041'], BioInterchange: ['#FBEAF0', '#72243E'], Standard: ['#F1EFE8', '#444441'],
}

// D-UI-2: header funnels. The list is SERVER-paged, so a funnel only collects what was asked and
// GET /api/drug-reference applies it in its COUNT and its page query. The clinical columns
// (Archetype ... Freq) are shown from the linked formulary row; their filter reads that same row.
const CLINICAL_COLS: ServerFunnelColumn[] = [
  { key: 'therapy_archetype', label: 'Archetype', kind: 'list', options: ['nurse_administered', 'dispense_only'] },
  { key: 'nurse_needed', label: 'Nurse', kind: 'list', options: ['Yes', 'No', 'Tech only'] },
  { key: 'pump_required', label: 'Pump', kind: 'list', options: ['Yes', 'No'] },
  { key: 'enrollment_signature', label: 'Enroll. sig', kind: 'list', options: ['Yes', 'No'] },
  { key: 'observation_minutes', label: 'Obs', kind: 'number' },
  { key: 'baseline_analytes', label: 'Baseline labs', kind: 'text' },
  { key: 'rescreen_analytes', label: 'Re-screen', kind: 'text' },
  { key: 'rescreen_frequency', label: 'Freq', kind: 'list', options: ['weekly', 'monthly', 'quarterly', 'semi-annually', 'yearly'] },
]
const FUNNEL_COLS: ServerFunnelColumn[] = [
  { key: 'title', label: 'Title', kind: 'text' },
  { key: 'generic_name', label: 'Generic', kind: 'text' },
  { key: 'brand_name', label: 'Brand', kind: 'text' },
  { key: 'category', label: 'Category', kind: 'text' },
  { key: 'gcn', label: 'GCN', kind: 'text' },
  { key: 'ndc', label: 'NDC', kind: 'text' },
  { key: 'rems_program', label: 'REMS', kind: 'text' },
  { key: 'substitution_targets', label: 'Substitution', kind: 'text' },
  { key: 'daw_flags', label: 'DAW (Pioneer)', kind: 'text' },
  { key: 'days_before_procedure', label: 'Days before proc.', kind: 'number' },
  { key: 'copay_program', label: 'Copay assist.', kind: 'text' },
  { key: 'care_plan_frequency', label: 'Care plan freq.', kind: 'text' },
  { key: 'is_active', label: 'Active', kind: 'list', options: ['Yes', 'No'] },
]
// Header label -> funnel key, in display order.
const LIST_HEADERS: [string, string][] = FUNNEL_COLS.map(c => [c.label, c.key])

// D-CRX-80 PR1: the analyte vocabulary lives in lib/analytes. This page kept its own copy and
// DrugRecordEditV4 kept a third — and the editor's was the one writing to the database, in the
// wrong shape. The grid normalizes on read so a row holding a legacy spelling still pills correctly.
//
// The legacy DrugModal editor and every constant that fed it were deleted here: it sat behind
// `LEGACY_EDITOR = false` — a compile-time constant, so the branch was unreachable — and it was
// hiding fifteen unused declarations that only the compiler could see once the branch went.

export default function DrugReferencePage({ onScreenEnter }: { onScreenEnter?: () => void }) {
  const [cats, setCats] = useState<Category[]>([])
  const [cols, setCols] = useState<ColumnDef[]>([])
  const [err, setErr] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [catFilter, setCatFilter] = useState('')
  const [srcFilter, setSrcFilter] = useState('')
  const [activeOnly, setActiveOnly] = useState(true)
  const [editing, setEditing] = useState<Partial<DrugRefRow> | null>(null)
  const [showCol, setShowCol] = useState(false)
  const [showColors, setShowColors] = useState(false)
  const [busy, setBusy] = useState(false)
  // D-CRX-42 WS1: values for the pharmacy's custom columns, keyed drugId -> fieldDefId (break #1 fix)
  const [cells, setCells] = useState<Record<string, Record<string, string | number | boolean | null>>>({})
  // D-CRX-42 G-11: linked-formulary clinical set per drug id (absent key = drug not linked).
  const [clin, setClin] = useState<Record<string, DrugRefClinical>>({})
  // Every DrugReference write (drug, clinical set, labs, protocols, categories, columns, sync) is
  // RequireOneOf("admin") on the server. Anyone else who can open this page gets it read-only, rather
  // than buttons that fill a whole form and then 403 on Save.
  const { role } = useAuth()
  const canEdit = role === 'admin'

  useEffect(() => { onScreenEnter?.() }, [onScreenEnter])

  // Categories and column defs are small and filter-independent — fetched once.
  useEffect(() => {
    void (async () => {
      try {
        const [c, k] = await Promise.all([api.listDrugCategories(), api.listDrugRefColumns()])
        setCats(c.items); setCols(k.items)
      } catch (e) { setErr(e instanceof Error ? e.message : 'Failed to load DrugReference') }
    })()
  }, [])

  // D-PERF-1: the catalogue is SERVER-paged and server-filtered. It used to fetch every row for
  // the pharmacy and slice it in the browser, which is the whole table over the wire on a tenant
  // with thousands of drugs — and a filter that only ever saw the rows already fetched.
  // 500ms, not 300. The search now also probes the dispensing history to find a drug by the pack
  // NDC in the pharmacist's hand, so a query costs more than a scan of the catalogue's own columns
  // and a mid-word fire is wasted work on a 0.5-1 vCore tenant DB (E-14).
  const rawDebouncedQ = useDebouncedValue(q, 500)
  // A one- or two-character term matches almost every drug, which is the most expensive possible
  // query and the least useful answer. Below three characters the list simply shows everything,
  // exactly as it does with an empty box, and no query is sent at all.
  const debouncedQ = rawDebouncedQ.trim().length >= 3 ? rawDebouncedQ : ''
  const [reloadTick, setReloadTick] = useState(0)
  // Catalogue columns, the clinical columns, then the pharmacy's own columns ("cf:<field id>").
  const funnelCols = useMemo<ServerFunnelColumn[]>(() => [
    ...FUNNEL_COLS, ...CLINICAL_COLS,
    ...cols.map((c): ServerFunnelColumn => c.field_type === 'checkbox'
      ? { key: `cf:${c.id}`, label: c.field_name, kind: 'list', options: ['Yes', 'No'] }
      : { key: `cf:${c.id}`, label: c.field_name, kind: 'text' }),
  ], [cols])
  const fx = useServerFunnels(funnelCols)
  const paged = useServerPaged<DrugRefRow>(
    ({ page, pageSize }) => api.listDrugReference({
      page, pageSize, q: debouncedQ,
      category: catFilter || undefined,
      source: srcFilter || undefined,
      includeInactive: !activeOnly,
      filter: fx.filter,
    }).then(async r => {
      // Auto-backfill: on a genuinely empty catalogue (not merely an empty page or a filter that
      // matches nothing) pull drug identity from the PioneerRx feed once, then re-list.
      // Sync is admin-only, so it is not even tried for staff: they see whatever admins synced.
      // A funnel that matches nothing is a filter that matched nothing, not an empty catalogue.
      const empty = (r.total ?? r.count) === 0 && !debouncedQ && !catFilter && !srcFilter && !fx.active
      if (empty && canEdit) {
        try {
          await api.syncDrugReferenceFromRx()
          r = await api.listDrugReference({ page, pageSize, includeInactive: !activeOnly })
        } catch { /* non-admin or no feed */ }
      }
      setCells((r.custom_values ?? {}) as Record<string, Record<string, string | number | boolean | null>>)
      setClin(r.clinical ?? {})
      setErr(null)
      return { items: r.items, total: r.total, page: r.page ?? page, page_size: r.page_size ?? pageSize, total_pages: r.total_pages }
    }).catch(e => {
      setErr(e instanceof Error ? e.message : 'Failed to load DrugReference')
      return { items: [], total: 0, page: 1, page_size: pageSize, total_pages: 1 }
    }),
    DEFAULT_PAGE_SIZE,
    [debouncedQ, catFilter, srcFilter, activeOnly, reloadTick, fx.sig],
  )
  // Refetch the current query (used by every save/close handler, some of which expect a promise).
  const load = useCallback(async () => { setReloadTick(t => t + 1) }, [])

  // No client-side filtering: the query above IS the filter, so what you see is the whole match
  // set, not the part of it that happened to be fetched.
  const pageRows = paged.rows
  const total = paged.total ?? paged.rows.length

  // G-11 fan-out save (WS16): one Save writes identity to drug_reference AND, when the drug is
  // linked to CareRx formulary rows, the clinical set to every linked row.
  async function save(row: Partial<DrugRefRow>, clinical?: Omit<DrugRefClinical, 'link_count'>) {
    setBusy(true)
    try {
      const res = await api.upsertDrugReference(row)
      const id = row.id ?? res.id
      if (clinical && id) await api.setDrugRefClinical(id, clinical)  // auto-links when no formulary row yet
      setEditing(null); await load()
    }
    catch (e) { alert(e instanceof Error ? e.message : 'Save failed') }
    finally { setBusy(false) }
  }
  async function toggleActive(r: DrugRefRow) {
    try { await api.upsertDrugReference({ id: r.id, is_active: !r.is_active }); await load() }
    catch (e) { alert(e instanceof Error ? e.message : 'Could not update') }
  }
  async function setCategory(r: DrugRefRow, category: string) {
    try { await api.upsertDrugReference({ id: r.id, category }); await load() }
    catch (e) { alert(e instanceof Error ? e.message : 'Could not set category') }
  }
  async function sync() {
    setBusy(true)
    try { const res = await api.syncDrugReferenceFromRx(); await load(); alert(`Synced ${res.rows} drug row(s) from the PioneerRx feed.`) }
    catch (e) { alert(e instanceof Error ? e.message : 'Sync failed') }
    finally { setBusy(false) }
  }
  async function addCategory() {
    const name = prompt('New category name (e.g. LimitedDistribution)')?.trim()
    if (!name) return
    try { await api.addDrugCategory(name); await load() } catch (e) { alert(e instanceof Error ? e.message : 'Failed') }
  }

  const contrastText = (hex: string): string => {
    const h = hex.replace('#', '')
    const v = h.length === 3 ? h.split('').map(x => x + x).join('') : h
    const r = parseInt(v.slice(0, 2), 16), g = parseInt(v.slice(2, 4), 16), b = parseInt(v.slice(4, 6), 16)
    return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#1a1a1a' : '#ffffff'
  }
  const catBadge = (c: string | null, needs: boolean) => {
    if (needs || !c) return <span style={{ fontSize: 12, background: T.warnBg, color: T.warnFg, border: `0.5px solid ${T.warnFg}`, padding: '2px 8px', borderRadius: 20 }}>Needs classification</span>
    const stored = cats.find(x => x.name === c)?.color
    const [bg, fg] = stored ? [stored, contrastText(stored)] : (CAT_COLOR[c] ?? ['#F1EFE8', '#444441'])
    return <span style={{ fontSize: 12, background: bg, color: fg, padding: '2px 9px', borderRadius: 20 }}>{c}</span>
  }

  return (
    <div style={{ padding: '1rem 1.25rem', color: T.text }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>DrugReference</h1>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: T.soft }}>Read/write reference table · Pioneer-fed + manual · feeds workflow rules by GCN</p>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={sync} disabled={busy} style={btn}>Sync from PioneerRx</button>
            <button onClick={() => setShowCol(true)} style={btn}>Add column</button>
            <button onClick={() => setEditing({ is_active: true })} style={btnPrimary}>Add drug</button>
          </div>
        )}
      </div>
      {!canEdit && (
        <Notice severity="info">
          Read-only. Changing a drug, its labs or its protocols needs a pharmacy admin.
        </Notice>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Say when a term is too short to search, rather than letting a typed box that changes
            nothing read as a broken filter. */}
        <span style={{ display: 'inline-flex', flexDirection: 'column', flex: 1, minWidth: 200 }}>
          <input placeholder="Search name, GCN, or NDC (3+ characters)" value={q}
                 onChange={e => setQ(e.target.value)} style={{ ...input, width: '100%' }} />
          {q.trim().length > 0 && q.trim().length < 3 && (
            <span style={{ fontSize: 11, color: T.soft, marginTop: 2 }}>Keep typing — 3 characters to search</span>
          )}
        </span>
        <SearchableSelect value={catFilter} onChange={setCatFilter} ariaLabel="Filter by category" placeholder="All categories" style={{ minWidth: 170 }}
          options={[{ value: '', label: 'All categories' }, { value: '__unclassified', label: 'Needs classification' }, ...cats.map(c => ({ value: c.name, label: c.name }))]} />
        {canEdit && <button onClick={addCategory} style={{ ...btn, fontSize: 12 }} title="Add a category">+ category</button>}
        {canEdit && <button onClick={() => setShowColors(true)} style={{ ...btn, fontSize: 12 }} title="Color-code categories">Colors</button>}
        <SearchableSelect value={srcFilter} onChange={setSrcFilter} ariaLabel="Filter by source" placeholder="All sources" style={{ minWidth: 150 }}
          options={[{ value: '', label: 'All sources' }, { value: 'pioneer', label: 'Pioneer' }, { value: 'manual', label: 'Manual' }]} />
        <label style={{ fontSize: 13, color: T.soft, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} /> Active only
        </label>
      </div>

      {err && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{err}</div>}
      {/* With a funnel active the table stays mounted while it refetches: the open funnel lives in
          the header, and swapping the table for "Loading…" would close it mid-edit. */}
      {paged.loading && total === 0 && !fx.active ? <div style={{ color: T.soft }}>Loading…</div> : (
        <div style={{ ...listScrollStyle(),
  overflowX: 'auto', border: `0.5px solid ${T.border}`, borderRadius: 12, background: T.surface }}>
          <Pager {...paged.pagerProps} label="drugs" />
          {fx.chips}
          <table {...fx.tableProps} style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13, whiteSpace: 'nowrap' }}>
            <thead>
              <tr style={{ borderBottom: `0.5px solid ${T.border}`, background: 'var(--color-surface-soft, #f9fafb)' }}>
                {LIST_HEADERS.map(([h, key]) =>
                  <th key={h} style={th}>{h}{fx.funnel(key)}</th>)}
                {/* D-CRX-42 G-11 (mockup): clinical columns from the linked formulary row, read-only in the grid */}
                {CLINICAL_COLS.map(c =>
                  <th key={c.key} style={th}>{c.label}{fx.funnel(c.key)}</th>)}
                {cols.map(c => <th key={c.id} style={th}>{c.field_name}{fx.funnel(`cf:${c.id}`)}</th>)}
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(r => (
                <React.Fragment key={r.id}>
                <tr style={{ borderBottom: `0.5px solid ${T.border}`, background: r.needs_classification ? T.warnBg : undefined }}>
                  <td style={td}>
                    <span title={r.source === 'pioneer' ? 'Pioneer-sourced' : 'Manual'} style={{ width: 6, height: 6, borderRadius: 6, display: 'inline-block', marginRight: 7, background: r.source === 'pioneer' ? '#185FA5' : '#B4B2A9' }} />
                    <button onClick={() => setEditing(r)} style={linkBtn}>{r.medication_name || r.generic_name || '—'}</button>
                  </td>
                  <td style={td}>{r.generic_name || '—'}</td>
                  <td style={td}>{r.brand_name || '—'}</td>
                  <td style={td}>
                    <div style={{ marginBottom: 2 }}>{catBadge(r.category, r.needs_classification)}</div>
                    {canEdit && <SearchableSelect value={r.needs_classification ? '' : (r.category ?? '')} onChange={v => v && setCategory(r, v)} ariaLabel="Set category" placeholder="— set —" style={{ minWidth: 150 }}
                      options={cats.map(c => ({ value: c.name, label: c.name }))} />}
                  </td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.gcn || '—'}</td>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.ndc || '—'}</td>
                  <td style={td}>{r.rems_program || '—'}</td>
                  <td style={td}>{r.substitution_targets || '—'}</td>
                  <td style={td}>{r.daw_flags == null ? '—' : r.daw_flags.split(',').map(f => f.trim()).filter(Boolean).map(f => (
                    <span key={f} title={`DawCodeID ${f} — from Pioneer`} style={{ ...pill, cursor: 'default', marginRight: 3,
                      background: f === '0' ? '#E1F5EE' : '#fef3c7', color: f === '0' ? '#085041' : '#92400e' }}>
                      {f === '0' ? '0 · Sub. permitted' : `${f} · DAW`}</span>))}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{r.days_before_procedure ?? '—'}</td>
                  <td style={td}>{r.copay_program || '—'}</td>
                  <td style={td}>{r.care_plan_frequency || '—'}</td>
                  <td style={td}>
                    <button onClick={() => toggleActive(r)} disabled={!canEdit} style={{ ...pill, background: r.is_active ? '#E1F5EE' : '#F1EFE8', color: r.is_active ? '#085041' : '#444441', cursor: canEdit ? 'pointer' : 'default' }}>{r.is_active ? 'Yes' : 'No'}</button>
                  </td>
                  {/* G-11: read-only clinical columns (edit in the dialog; save fans out to linked formulary rows) */}
                  {(() => {
                    const cl = clin[r.id!]
                    const yn = (v: boolean | null | undefined) => v == null ? '—' : v ? 'Yes' : 'No'
                    // D-CRX-80 PR1: normalize before labelling — a row still holding V4's
                    // label-shaped values renders "TB", not "TB SCREENING (IGRA)".
                    const pills = (xs: string[] | undefined) => !xs || xs.length === 0 ? '—' : (
                      <span>{normalizeAnalytes(xs).map(t => <span key={t} style={{ ...pill, background: '#F1EFE8', color: '#444441', cursor: 'default', marginRight: 3 }}>{analytePill(t)}</span>)}</span>)
                    const soft = { ...td, color: cl ? undefined : T.soft }
                    return (<>
                      <td style={soft}>{cl ? (cl.therapy_archetype === 'dispense_only' ? <b>dispense_only</b> : 'nurse_administered') : '—'}</td>
                      <td style={soft}>{cl?.nurse_needed === 'tech_only' ? 'Tech only' : cl?.nurse_needed === 'yes' ? 'Yes' : cl?.nurse_needed === 'no' ? 'No' : '—'}</td>
                      <td style={soft}>{cl ? yn(cl.pump_required) : '—'}</td>
                      <td style={soft}>{cl ? yn(cl.requires_enrollment_signature) : '—'}</td>
                      <td style={{ ...soft, textAlign: 'center' }}>{cl?.observation_minutes ?? '—'}</td>
                      <td style={soft}>{pills(cl?.baseline_analytes)}</td>
                      <td style={soft}>{pills(cl?.rescreen_analytes)}</td>
                      <td style={soft}>{cl?.rescreen_frequency ? cl.rescreen_frequency.replace('_', '-') : '—'}</td>
                    </>)
                  })()}
                  {cols.map(c => {
                    const v = cells[r.id!]?.[c.id]
                    const disp = v == null || v === '' ? '—' : c.field_type === 'checkbox' ? (v ? 'Yes' : 'No') : String(v)
                    return <td key={c.id} style={{ ...td, color: v == null ? T.soft : undefined }}>{disp}</td>
                  })}
                  <td style={td}><button onClick={() => setEditing(r)} style={{ ...btn, fontSize: 12, padding: '2px 8px' }}>{canEdit ? 'Edit' : 'View'}</button></td>
                </tr>
                </React.Fragment>
              ))}
              {pageRows.length === 0 && <tr><td colSpan={22 + cols.length} style={{ ...td, color: T.soft, textAlign: 'center', padding: 24 }}>{paged.loading ? 'Loading…' : 'No drugs match this view. Clear the filters, add one, or sync from the PioneerRx feed.'}</td></tr>}
            </tbody>
          </table>
          <Pager {...paged.pagerProps} showPageSize={false} label="drugs" />
        </div>
      )}

      {editing && (
        <DrugRecordEditV4 row={editing} cats={cats} cols={cols} cells={editing.id ? (cells[editing.id] ?? {}) : {}}
          clinical={editing.id ? (clin[editing.id] ?? null) : null} busy={busy} readOnly={!canEdit} onClose={() => setEditing(null)} onSave={save}
          onCell={async (fieldDefId, patch) => {
            if (!editing.id) return
            try { await api.setDrugRefCell(editing.id, { field_def_id: fieldDefId, ...patch }); await load() }
            catch (e) { alert(e instanceof Error ? e.message : 'Failed to save value') }
          }} />
      )}
      {showCol && <ColumnModal onClose={() => setShowCol(false)} onSaved={async () => { setShowCol(false); await load() }} />}
      {showColors && <CategoryColorsModal cats={cats} fallback={CAT_COLOR} onClose={() => setShowColors(false)} onSaved={load} />}
    </div>
  )
}

function CategoryColorsModal({ cats, fallback, onClose, onSaved }: { cats: Category[]; fallback: Record<string, [string, string]>; onClose: () => void; onSaved: () => Promise<void> }) {
  const [busyName, setBusyName] = useState<string | null>(null)
  async function setColor(name: string, color: string | null) {
    setBusyName(name)
    try { await api.setDrugCategoryColor(name, color); await onSaved() }
    catch (e) { alert(e instanceof Error ? e.message : 'Could not save color') }
    finally { setBusyName(null) }
  }
  return (
    <Overlay onClose={onClose}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 500 }}>Category colors</h2>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--color-text-soft, #6b7280)' }}>Each category gets its own badge color, used everywhere categories display. Reset returns a category to the default palette.</p>
      {cats.map(c => {
        const effective = c.color ?? (fallback[c.name]?.[0] ?? '#F1EFE8')
        return (
          <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 2px', borderBottom: '0.5px solid var(--color-border, #eee)' }}>
            <span style={{ fontSize: 12, background: effective, color: '#1a1a1a', padding: '2px 10px', borderRadius: 20, minWidth: 90, textAlign: 'center' }}>{c.name}</span>
            <input type="color" value={effective} disabled={busyName === c.name}
              onChange={e => setColor(c.name, e.target.value)}
              style={{ width: 40, height: 26, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }} aria-label={`Color for ${c.name}`} />
            <span style={{ fontSize: 11, color: 'var(--color-text-soft, #6b7280)', fontFamily: 'monospace' }}>{c.color ?? 'default'}</span>
            {c.color && <button onClick={() => setColor(c.name, null)} disabled={busyName === c.name} style={{ fontSize: 11, marginLeft: 'auto', border: 'none', background: 'none', color: 'var(--color-primary, #185FA5)', cursor: 'pointer' }}>Reset</button>}
          </div>
        )
      })}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
        <button onClick={onClose} style={{ fontSize: 13, padding: '7px 12px', border: '0.5px solid var(--color-border, #d1d5db)', borderRadius: 8, background: 'transparent', cursor: 'pointer' }}>Done</button>
      </div>
    </Overlay>
  )
}


function ColumnModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(''); const [type, setType] = useState('text'); const [opts, setOpts] = useState('')
  async function save() {
    if (!name.trim()) return
    try { await api.addDrugRefColumn({ name: name.trim(), type, dropdown_options: type === 'dropdown' ? JSON.stringify(opts.split(',').map(s => s.trim()).filter(Boolean)) : undefined }); onSaved() }
    catch (e) { alert(e instanceof Error ? e.message : 'Failed') }
  }
  return (
    <Overlay onClose={onClose}>
      <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 500 }}>Add custom column</h2>
      <label style={fieldLabel}>Column name<input value={name} onChange={e => setName(e.target.value)} style={input} /></label>
      <label style={fieldLabel}>Type
        <SearchableSelect value={type} onChange={setType} ariaLabel="Column type"
          options={['text', 'dropdown', 'date', 'number', 'checkbox'].map(t => ({ value: t, label: t }))} />
      </label>
      {type === 'dropdown' && <label style={fieldLabel}>Options (comma-separated)<input value={opts} onChange={e => setOpts(e.target.value)} style={input} /></label>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button onClick={onClose} style={btn}>Cancel</button><button onClick={save} style={btnPrimary}>Add</button>
      </div>
    </Overlay>
  )
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--color-surface, #fff)', borderRadius: 12, padding: '1.25rem', width: 460, maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>{children}</div>
    </div>
  )
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontWeight: 500, fontSize: 13 }
const td: React.CSSProperties = { padding: '9px 12px' }
const input: React.CSSProperties = { width: 'auto', minWidth: 120, height: 34, padding: '0 10px', border: '0.5px solid var(--color-border, #e5e7eb)', borderRadius: 8, background: 'var(--color-surface, #fff)', color: 'var(--color-text, #1a1a1a)' }
const btn: React.CSSProperties = { fontSize: 13, padding: '7px 12px', border: '0.5px solid var(--color-border, #d1d5db)', borderRadius: 8, background: 'transparent', color: 'var(--color-text, #1a1a1a)', cursor: 'pointer' }
const btnPrimary: React.CSSProperties = { ...btn, background: 'var(--color-primary, #185FA5)', color: '#fff', border: 'none' }
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--color-primary, #185FA5)', cursor: 'pointer', padding: 0, fontSize: 13 }
const pill: React.CSSProperties = { fontSize: 11, padding: '2px 10px', borderRadius: 20, border: 'none', cursor: 'pointer' }
const fieldLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: 'var(--color-text-soft, #6b7280)', marginBottom: 10 }
